// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IntlShape, injectIntl } from 'react-intl';
import { typeof History, withRouter } from 'react-router-dom';
import { Box } from 'grommet';
import { isEmpty, memoize } from 'lodash';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI, withConfirmation } from 'speed-js-react';
import CableForm from 'esm-web-pro/containers/pages/network/objects/cable/create/CableForm';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import MapObjectForm from 'esm-web-pro/containers/map/forms/MapObjectForm';
import type { Confirmation } from 'speed-js-react';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import type { Cable } from 'esm-web-pro/types/networks';
import { pointsCoordinatesHighlightCallback } from 'esm-web-pro/containers/pages/network/highlight';
import Spinner from 'esm-web-pro/components/forms/Spinner';


const getCableInitial = memoize((cable: Object) => {
    const result = { ...cable };
    result.fiber_count = result.fiber_count.code;
    excludeFields.forEach((field) => delete result[field]);
    return result;
});

type Props = {
    id: string,
    cable: Cable,
    onClose: Function,
    onSave: Function,
    updateObject?: Function,
    mapUuid: string,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    history: History,
    match: Object,
    setSensitivity: (key: string, sensitivity: boolean) => void,
    editor: BaseEditor,
    getCableLinesForPaths: (uuid: string, pathIds: Array<integer>) => Promise<Object>,
}
const excludeFields = ['infrastructure_provider', 'tube_count', 'path', 'fiber_count',
    'start_termination_point', 'end_termination_point', 'length', 'stage'];

const CableEditor = (props: Props) => {
    const formRef = React.useRef(null);
    const formContainerRef = React.useRef(null);
    const {
        cable: { cable_path: { cable_paths: cablePaths, cable_reserves: cableReserves } },
        cable,
        onClose,
        onSave,
        updateObject,
        mapUuid,
        confirm, intl: { formatMessage },
        history,
        match,
        id,
        setSensitivity,
        editor,
        getCableLinesForPaths,
    } = props;
    const [points, setPoints] = React.useState([]);
    const [lines, setLines] = React.useState([]);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [saveIsDisabled, setSaveIsDisabled] = React.useState(false);
    // Since we fetch all cable path options from the API this state is used to show a spinner while
    // the data is being fetched
    const [loadingCablePaths, setLoadingCablePaths] = React.useState(true);
    const [cablePathOptions, setCablePathOptions] = React.useState(null);

    const scrollToTop = () => {
        // If the form with cables is too long we need to scroll to the top
        // of the form to see the error messages
        formContainerRef.current.scrollIntoView({ behavior: 'smooth' });
    };

    React.useEffect(() => {
        const newPoints = [];
        const newLines = [];
        let lastContinuousPipeIndex;
        let pointNumber = 0;
        let segmentNumber;

        if (cablePathOptions !== null) {
            cablePaths.forEach((cablePath, i) => {
                // The cable path options are fetched from the API and are not in the same order as
                // the cable paths. We need to filter the options to get the options for the current
                // cable path
                const availablePathContainerOptions = cablePathOptions.filter((
                    option,
                ) => option.cable_path_id === cablePath.id);

                if (cablePath.continuous_pipe
                        && (i > 0 && cablePath.continuous_pipe === cablePaths[i - 1].continuous_pipe)
                        && newLines[lastContinuousPipeIndex]
                ) {
                    const key = Object.keys(newLines[lastContinuousPipeIndex].options)[0];
                    const pipes = newLines[lastContinuousPipeIndex].options[key]?.pipes;
                    newLines[lastContinuousPipeIndex].options[key].pipes = {
                        ...pipes,
                        [cablePath.uuid]: {
                            name: cablePath.container_name,
                        },
                    };
                    newPoints.push({
                        reserve: cableReserves[i].length,
                        continuousPipe: true,
                        pointNumber: Number(`${pointNumber}.${segmentNumber}`),
                        coordinates: cableReserves[i].coordinates.coordinates,
                        uuid: cableReserves[i].uuid,
                    });
                    segmentNumber += 1;
                } else {
                    pointNumber += 1;
                    // We need to construct a new object for each cable path in the correct format
                    // as options are different for each cable path depending on the container type
                    const containerOptionsForPath = {};
                    availablePathContainerOptions.forEach((choice) => {
                        if (choice.continuous_pipe) {
                            containerOptionsForPath[choice.uuid] = {
                                name: choice.name,
                                pipes: {
                                    [choice.uuid]: {
                                        name: choice.name,
                                    },
                                },
                            };
                        } else {
                            containerOptionsForPath[choice.uuid] = {
                                name: choice.name,
                            };
                        }
                    });

                    if (cablePath.continuous_pipe) {
                        lastContinuousPipeIndex = i;
                    }

                    newLines.push({
                        value: cablePath.container_uuid,
                        options: containerOptionsForPath,
                        uuid: cablePath.uuid,
                        startNumber: pointNumber,
                        providerRef: cablePath.provider_ref,
                    });

                    newPoints.push({
                        reserve: cableReserves[i].length,
                        pointNumber,
                        coordinates: cableReserves[i].coordinates.coordinates,
                        uuid: cableReserves[i].uuid,
                    });

                    segmentNumber = 1;
                }
            });
            newPoints.push({
                reserve: cableReserves[cableReserves.length - 1].length,
                uuid: cableReserves[cableReserves.length - 1].uuid,
                pointNumber: pointNumber + 1,
                coordinates: cableReserves[cableReserves.length - 1].coordinates.coordinates,
            });

            setPoints(newPoints);
            setLines(newLines);
            setLoadingCablePaths(false);
        }
    }, [cablePathOptions, cablePaths, cableReserves]);


    React.useEffect(() => {
        let active = true;

        setCablePathOptions(null);
        setLoadingCablePaths(true);
        const cablePathIds = cablePaths.map((cablePath) => cablePath.id);

        getCableLinesForPaths(cable.uuid, cablePathIds, mapUuid).then((data) => {
            if (active) {
                setCablePathOptions(data.results);
            }
        });

        // Prevent additional api calls when the component is unmounted
        return () => {
            active = false;
        };
    }, [setCablePathOptions, cable.uuid, cablePaths, mapUuid, getCableLinesForPaths]);

    React.useEffect(() => {
        const pointsNumbers = points.map((p) => p.pointNumber);
        const highlightPointsCallback = pointsCoordinatesHighlightCallback(
            points.map((p) => [p.coordinates[1], p.coordinates[0]]), null, pointsNumbers,
        );
        editor.registerLoadingCallback(highlightPointsCallback);
        editor.reloadFeatures();
        return () => {
            editor.unregisterLoadingCallback(highlightPointsCallback);
        };
    }, [points.length, lines.length]);

    const handleClose = () => {
        onClose();
    };

    const handleSave = () => {
        let formData = {};
        if (formRef.current) {
            const { touched } = formRef.current.getFormikBag();
            setIsSubmitting(true);

            if (!isEmpty(touched)) {
                formData = formRef.current.getFormikBag().values;
                formRef.current.getFormikBag().validateForm();
                const { errors } = formRef.current.getFormikBag();
                if (!isEmpty(errors)) {
                    setIsSubmitting(false);
                    return;
                }
            }
        }

        const reservesList = points.filter((p) => !p.continuousPipe).map((p) => ({
            uuid: p.uuid,
            length: p.reserve,
        }));

        const cablePathsValues = lines.reduce((linesObj, l) => {
            linesObj[l.startNumber] = {
                container: l.value,
                cable_path: l.uuid,
                provider_ref: l.providerRef,
            };
            return linesObj;
        }, {});


        if (updateObject) {
            updateObject('cable.geo', cable.identity, mapUuid, {
                reserves: reservesList, cable_paths: cablePathsValues, ...formData,
            }).then((response) => {
                if (response.data && response.data.non_field_errors) {
                    formRef.current.getFormikBag().setFieldError(
                        'non_field_errors',
                        response.data.non_field_errors,
                    );
                    scrollToTop();
                } else {
                    onSave(response);
                }
                setIsSubmitting(false);
            });
        }
    };

    const handleFormChange = () => {
        if (formRef.current) {
            formRef.current.getFormikBag().validateForm();
        }
    };

    const handleReserveChange = (index, reserve) => {
        const newPoints = [...points];
        newPoints[index] = { ...newPoints[index], reserve };
        setPoints(newPoints);
    };

    const handleProviderRefChange = (index, providerRef) => {
        const newLines = [...lines];
        newLines[index] = { ...newLines[index], providerRef };
        setLines(newLines);
    };

    const handleLineChange = (index, choice) => {
        // Formik treats null as valid value for some mind-boggling reason
        if (choice === null) {
            setSaveIsDisabled(true);
        } else {
            setSaveIsDisabled(false);
        }

        const newLines = [...lines];
        newLines[index] = { ...newLines[index], value: choice };
        setLines(newLines);
    };

    return (points && lines && cable && !loadingCablePaths) ? (
        <>
            <CableForm
                lines={lines}
                points={points}
                onReserveChange={handleReserveChange}
                onProviderRefChange={handleProviderRefChange}
                onLineChange={handleLineChange}
                onSave={handleSave}
                onClose={handleClose}
                provider={cable.infrastructure_provider_entity_name}
                initialExport={cable.export}
                isSubmitting={isSubmitting}
                disableSave={saveIsDisabled}
            >
                <Box height="auto" flex={{ shrink: 0 }} pad="0px" ref={formContainerRef}>
                    <MapObjectForm
                        forwardRef={formRef}
                        resourceName="cable.geo"
                        resourceArguments={[mapUuid]}
                        initial={getCableInitial(cable)}
                        objectId={cable.identity}
                        excludeFields={excludeFields}
                        onChange={handleFormChange}
                        onInitialize={null}
                        overflow="hidden"
                        padding="0px"
                        height="100%"
                    />
                </Box>
            </CableForm>
        </>
    ) : (
        <Spinner
            type="clipLoader"
            loading
            blocking={false}
            color="black"
            coverStyle={{
                position: 'relative',
                top: '20px',
            }}
        />
    );
};

CableEditor.defaultProps = {
    updateObject: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    updateObject: {
        send: (resourceName, id, map, data) => apiClient.resources[resourceName].partialUpdate(map, id, data),
    },
    getCableLinesForPaths: {
        send: (
            uuid: string,
            paths: Array<integer>,
            map: String,
        ) => apiClient.resources.cable.getCableLinesForPaths(
            uuid,
            { paths, map },
        ),
    },
});

export default connect(null, (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch)))(
    injectIntl(
        withRouter(
            withConfirmation(
                connectAPI(apiCallback)(
                    CableEditor,
                ),
            ),
        ),
    ),
);
